import {type ReactNode} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {useCurrentAccountProfile} from '#/state/queries/useCurrentAccountProfile'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {type Trend} from '../data/trends'
import {useTrendPosts} from '../data/useTrendPosts'
import {trendHashtag} from '../util'

/**
 * Heading + clickable composer pill ("Add your opinion on the topic...") that
 * opens the composer prefilled with the trend's description + hashtag.
 *
 * Mirrors Figma nodes `384:53895` (the heading) and `384:53897` (the pill) in
 * file `C8J3bA4ThNVCG0vzlHeI16`. No outer horizontal padding — the caller
 * positions it. Reused by the trend-detail prompt and by the hero/featured
 * cards' empty (no-posts) state.
 */
export function TrendComposerPill({
  trend,
  headingText,
}: {
  trend: Trend
  headingText: ReactNode
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const {openComposer} = useOpenComposer()
  const profile = useCurrentAccountProfile()

  return (
    <View style={[a.gap_sm]}>
      <Text style={[a.text_sm, a.font_medium, t.atoms.text]}>
        {headingText}
      </Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`Add your opinion`)}
        accessibilityHint={_(msg`Opens the composer`)}
        // Prefill: blank line for the user's own words on top, then the
        // trend's title, its description, then its hashtag. The leading
        // newlines make the editor place the cursor at the start (see
        // TextInput.web.tsx); publish strips them again if the user posts
        // without writing.
        //
        // The title is included verbatim for now: the posts listed under a
        // trend come from a search on `trend.title` (see useTrendPosts), so a
        // post that doesn't contain the full title never shows up there.
        onPress={() =>
          openComposer({
            text: `\n\n${trend.title}\n\n${trend.description}\n\n${trendHashtag(trend)}`,
          })
        }
        style={[
          a.flex_row,
          a.align_center,
          a.gap_sm,
          a.rounded_full,
          a.border,
          {
            height: 44,
            paddingLeft: 6,
            paddingRight: 16,
            borderColor: ss.border.contrast_low,
            backgroundColor: ss.bg.default,
          },
        ]}>
        <UserAvatar type="user" size={29} avatar={profile?.avatar} />
        <Text
          numberOfLines={1}
          style={[
            a.flex_1,
            a.text_md,
            {lineHeight: 22},
            t.atoms.text_contrast_medium,
          ]}>
          <Trans>Add your opinion on the topic...</Trans>
        </Text>
      </Pressable>
    </View>
  )
}

/**
 * Composer prompt shown on trend detail, directly under the news-article
 * carousel (`FeaturedCardsCarousel`). Always shown; the heading adapts to
 * whether a discussion already exists: "Join the discussion" when the trend's
 * post search returns posts, otherwise the empty-state "Start the discussion".
 */
export function TrendComposerPrompt({trend}: {trend: Trend}) {
  // Mirror the trend's post search (by title) to know whether anyone has posted.
  // Until the search resolves we default to the "Start" heading.
  const {posts, isFetched} = useTrendPosts(trend, {limit: 1})
  const hasPosts = isFetched && posts.length > 0

  return (
    <View style={[a.w_full, a.px_lg, {paddingBottom: 16}]}>
      <TrendComposerPill
        trend={trend}
        headingText={
          hasPosts ? (
            <Trans>Join the discussion</Trans>
          ) : (
            <Trans>Start the discussion</Trans>
          )
        }
      />
    </View>
  )
}
